import {
  adjustContentVisibility,
  SectionParagraph as Subtitle,
  SectionTitle as Title,
} from '@/components/shared/Content';
import { Box, Flex, font, themedColor } from '@lemonade-hq/cdk';
import { Pill } from '@lemonade-hq/nuis';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis-atomic';
import parse from 'html-react-parser';
import type { HTMLAttributes } from 'react';
import React from 'react';
import styled from 'styled-components';
import {
  CoverageContainer,
  CoverageItem,
  CoverageItemDescription,
  CoverageItemImage,
  CoverageItemInfo,
  CoverageItemTitle,
  Section,
} from './StyledElements';

export const CoverageItemTitleText = styled.h3`
  text-align: center;
  text-wrap: balance;
  ${font('main', {
    fontSize: '2.0rem',
    lineHeight: '2.4rem',
    fontWeight: 'bold',
  })}

  ${MEDIA_QUERIES.tablet`
    ${font('main', {
      fontSize: '2.2rem',
      lineHeight: '3rem',
      fontWeight: 'bold',
    })}
  `}
  ${MEDIA_QUERIES.desktop`
    ${font('main', {
      fontSize: '2.6rem',
      lineHeight: '3.4rem',
      fontWeight: 'bold',
    })}
  `}
`;

const StyledTitle = styled(Title)`
  white-space: pre-line;
  ${MEDIA_QUERIES.tabletAndDesktop`
      white-space: normal;
    `}
`;

export const CoverageItemDescriptionText = styled.p`
  color: #797979;
  text-align: center;
  text-wrap: balance !important;
  ${font('main', {
    fontSize: '1.4rem',
    lineHeight: '1.9rem',
  })}

  ${MEDIA_QUERIES.tabletAndDesktop`
    ${font('main', {
      fontSize: '1.6rem',
      lineHeight: '2.1rem',
    })}
  `}
`;

type ThemedColor = Parameters<typeof themedColor>[0];

const StyledPill = styled(Pill)<{ backgroundColor?: ThemedColor }>`
  color: ${themedColor('white')};
  background-color: ${({ backgroundColor = 'primary' }): ReturnType<typeof themedColor> =>
    themedColor(backgroundColor)};
  position: absolute;
  top: 0;
  right: 50%;
  transform: translateX(calc(50% + 60px));
`;

const StyledSection = styled(Section)`
  ${adjustContentVisibility};
`;

export interface CarCoverage {
  ppm: Coverage[];
  fixed: Coverage[];
}

export interface Coverage {
  id?: string;
  title: string | string[];
  description: string | string[];
  alt: string;
  background: string | StaticImageData;
  backgroundDesktop?: string;
  altColorBackground?: string;
  altColorBackgroundDesktop?: string;
  preElement?: React.ReactElement;
  pill?: {
    text: string;
    backgroundColor?: ThemedColor;
  };
}

export interface CoverageSectionProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  subtitle: string;
  coverages: Coverage[];
  itemWidth?: number | [number, number] | [number, number, number];
  itemMargin?: number | [number, number] | [number, number, number];
  containerWidth?: number | [number, number] | [number, number, number];
  darkBackground?: boolean;
}

export const CoverageSection = React.forwardRef<HTMLDivElement, CoverageSectionProps>(
  ({ title, subtitle, coverages, itemMargin = 30, itemWidth = [210, 270], containerWidth, ...props }, ref) => {
    return (
      <StyledSection ref={ref} mb={0} pt={[45, 100]} width={1} contain="paint" {...props}>
        <Flex gap="20px" flexDirection="column">
          <StyledTitle tabIndex={0}>{parse(title)}</StyledTitle>
          <Box width={['80%', '100%']} mx="auto">
            <Subtitle tabIndex={0}>{parse(subtitle)}</Subtitle>
          </Box>
        </Flex>

        <CoverageContainer
          mt={[45, 80, 80]}
          px={[40, 0]}
          pb={[50, 100, 100, 100]}
          width={[1, 980, 1]}
          justifyContent={['flex-start', 'center']}
          flexWrap={['nowrap', 'wrap']}
          containerWidth={containerWidth}
          role="list"
        >
          {coverages.map(({ background, backgroundDesktop, title, description, alt, pill, preElement: sideNote }) => {
            const titles = typeof title === 'string' ? [title] : title;
            const descriptions = typeof description === 'string' ? [description] : description;

            return (
              <CoverageItem
                key={titles.join()}
                alignItems="center"
                position="relative"
                flexBasis={itemWidth}
                minWidth={itemWidth}
                mx={itemMargin}
                role="listitem"
              >
                {pill && <StyledPill backgroundColor={pill.backgroundColor}>{pill.text}</StyledPill>}
                {sideNote}
                <CoverageItemImage alt={alt} src={backgroundDesktop || background} width={165} height={165} />
                <CoverageItemInfo tabIndex={0}>
                  <CoverageItemTitle flexDirection="column">
                    {titles.map(title => (
                      <CoverageItemTitleText key={title}>{parse(title)}</CoverageItemTitleText>
                    ))}
                  </CoverageItemTitle>
                  <CoverageItemDescription flexDirection="column">
                    {descriptions.map(description => (
                      <CoverageItemDescriptionText key={description} style={{ whiteSpace: 'pre-line' }}>
                        {parse(description)}
                      </CoverageItemDescriptionText>
                    ))}
                  </CoverageItemDescription>
                </CoverageItemInfo>
              </CoverageItem>
            );
          })}
        </CoverageContainer>
      </StyledSection>
    );
  },
);
